import { useCallback, useEffect, useState } from 'react';
import { Contador, sinNeutro } from '../components/ProductosSeccion';
import { Alerta, Boton, ErrorApi, Guia, Pill } from '../components/ui';
import type {
  DocRef, DocumentoCr, DocumentoCrDetalle, ErrorCargaCr, EstadoDocumentoCr, FacturacionCrApi, FiltroDocumentosCr, PaginaCr, TipoDocumentoCr,
} from './types';

const ESTADO: Record<EstadoDocumentoCr, { texto: string; tono: 'ok' | 'pendiente' | 'error' | 'neutro'; ayuda: string }> = {
  PENDIENTE: { texto: 'Pendiente', tono: 'neutro', ayuda: 'En cola: se firma y se envía a Hacienda en los próximos minutos.' },
  EN_PROCESO: { texto: 'En Hacienda', tono: 'pendiente', ayuda: 'Hacienda lo recibió y lo está validando.' },
  REINTENTANDO: { texto: 'Reintentando', tono: 'pendiente', ayuda: 'Hacienda no respondió. El sistema lo vuelve a enviar solo.' },
  ACEPTADO: { texto: 'Aceptado', tono: 'ok', ayuda: 'Aceptado por Hacienda. Se envía al cliente por correo.' },
  RECHAZADO: { texto: 'Rechazado', tono: 'error', ayuda: 'Hacienda lo rechazó. Corrija la causa y use Emitir de nuevo.' },
  OTRO: { texto: 'Revisar', tono: 'error', ayuda: 'Respuesta no reconocida. Comuníquese con soporte.' },
};

const TIPO: Record<TipoDocumentoCr, string> = { '01': 'Factura', '04': 'Tiquete', '03': 'Nota de crédito', '02': 'Nota de débito' };

const iso = (d: Date) => d.toISOString().slice(0, 10);
const fechaCorta = (s: string | null) => (s ? new Date(s).toLocaleDateString('es-CR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fechaHora = (s: string | null) => (s ? new Date(s).toLocaleString('es-CR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const num = (n: number) => n.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dinero = (n: number, m: string | null) => `${m === 'USD' ? 'USD' : '₡'} ${num(n)}`;
const llave = (d: DocRef) => `${d.nFactura}-${d.tipo}-${d.idReconciliacion}`;

export function DocumentosCR({ api, idEmpresa }: { api: FacturacionCrApi; idEmpresa: number }) {
  const hoy = new Date();
  const [filtro, setFiltro] = useState<FiltroDocumentosCr>({ pagina: 1, desde: iso(new Date(hoy.getTime() - 30 * 86400000)), hasta: iso(hoy) });
  const [buscar, setBuscar] = useState('');
  const [pagina, setPagina] = useState<PaginaCr<DocumentoCr> | null>(null);
  const [errores, setErrores] = useState<ErrorCargaCr[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [abierto, setAbierto] = useState<DocRef | null>(null);
  const [recargada, setRecargada] = useState<number | null>(null);

  const cargar = useCallback(() => {
    setError(null);
    api.documentos(idEmpresa, filtro).then(setPagina).catch(setError);
    api.erroresCarga(idEmpresa).then(setErrores).catch(() => setErrores([]));
  }, [api, idEmpresa, filtro]);
  useEffect(cargar, [cargar]);

  const totalPaginas = pagina ? Math.max(1, Math.ceil(pagina.total / pagina.tamano)) : 1;
  const conteo = pagina?.conteo ?? {};
  const pendientesCarga = errores.filter((e) => e.puedeRecargar);

  return (
    <section className="seccion">
      <Guia
        id="cr-documentos"
        titulo="Cómo dar seguimiento a sus comprobantes"
        pasos={[
          <>Sus ventas se convierten solas en <b>factura</b> (si el cliente tiene identificación) o <b>tiquete</b> electrónico y se envían a Hacienda en pocos minutos.</>,
          <>
            Estados: <b>Pendiente</b> → <b>En Hacienda</b> → <b>Aceptado</b> (se envía por correo al cliente). Si Hacienda no responde, queda
            en <b>Reintentando</b> y el sistema lo reenvía solo.
          </>,
          <>
            Si queda <b>Rechazado</b>, abra el documento para ver el motivo, corrija la causa (por ejemplo, un código CABYS o un dato del
            emisor) y pulse <b>Emitir de nuevo</b>: se genera un comprobante nuevo con otro consecutivo. Un documento rechazado no se puede reenviar.
          </>,
          <>Abra un documento para descargar el <b>XML firmado</b> y la <b>respuesta de Hacienda</b>, o reenviar el correo al cliente.</>,
          <>
            <b>Facturas no cargadas</b>: ventas que no entraron a facturación electrónica por un error. Corrija la causa y pulse <b>Volver a cargar</b>.
          </>,
        ]}
      />

      <header className="seccion__cab">
        <div>
          <h2>Comprobantes electrónicos</h2>
          <p className="texto-sec">Facturas, tiquetes y notas del período seleccionado.</p>
        </div>
        <Boton tipo="secundario" onClick={cargar}>Actualizar</Boton>
      </header>

      <ErrorApi error={error} />
      {recargada !== null && <Alerta tipo="exito" titulo={`La venta ${recargada} se cargó y se enviará a Hacienda en los próximos minutos.`} />}
      {pendientesCarga.length > 0 && (
        <NoCargadas api={api} idEmpresa={idEmpresa} errores={pendientesCarga} onCambio={(e, n) => { setErrores(e); setRecargada(n); cargar(); }} />
      )}

      <div className="contadores">
        <Contador valor={Object.values(conteo).reduce((a, b) => a + b, 0)} texto="Todos" activo={!filtro.estado} onClick={() => setFiltro({ ...filtro, estado: '', pagina: 1 })} />
        {(['PENDIENTE', 'EN_PROCESO', 'ACEPTADO', 'RECHAZADO', 'REINTENTANDO'] as EstadoDocumentoCr[]).map((e) => (
          <Contador key={e} valor={conteo[e] ?? 0} texto={ESTADO[e].texto} tono={sinNeutro(ESTADO[e].tono)}
            activo={filtro.estado === e} onClick={() => setFiltro({ ...filtro, estado: e, pagina: 1 })} />
        ))}
      </div>

      <form className="filtros" onSubmit={(e) => { e.preventDefault(); setFiltro({ ...filtro, buscar: buscar.trim(), pagina: 1 }); }}>
        <select aria-label="Tipo de comprobante" value={filtro.tipo ?? ''} onChange={(e) => setFiltro({ ...filtro, tipo: e.target.value as TipoDocumentoCr | '', pagina: 1 })}>
          <option value="">Todos los tipos</option>
          {(Object.keys(TIPO) as TipoDocumentoCr[]).map((t) => <option key={t} value={t}>{TIPO[t]}</option>)}
        </select>
        <input className="fecha" type="date" aria-label="Desde" value={filtro.desde ?? ''} max={filtro.hasta} onChange={(e) => e.target.value && setFiltro({ ...filtro, desde: e.target.value, pagina: 1 })} />
        <input className="fecha" type="date" aria-label="Hasta" value={filtro.hasta ?? ''} min={filtro.desde} onChange={(e) => e.target.value && setFiltro({ ...filtro, hasta: e.target.value, pagina: 1 })} />
        <input placeholder="N.º de venta, clave, consecutivo o cliente" value={buscar} onChange={(e) => setBuscar(e.target.value)} aria-label="Buscar comprobante" />
        <button className="btn btn--secundario" type="submit">Buscar</button>
      </form>

      {!pagina && !error && <div className="cargando">Cargando comprobantes…</div>}
      {pagina && pagina.items.length === 0 && <div className="vacio">No hay comprobantes con estos filtros.</div>}
      {pagina && pagina.items.length > 0 && (
        <div className="tabla-scroll">
          <table className="tabla tabla--clic">
            <thead>
              <tr><th>Fecha</th><th>Comprobante</th><th>Venta</th><th>Cliente</th><th className="num">Total</th><th>Estado</th></tr>
            </thead>
            <tbody>
              {pagina.items.map((d) => (
                <tr key={llave(d)} onClick={() => setAbierto(d)} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setAbierto(d)}>
                  <td>{fechaCorta(d.fecha)}</td>
                  <td>{TIPO[d.tipo]}<div className="mono texto-sec">{d.consecutivo ?? 'sin consecutivo aún'}</div></td>
                  <td className="mono">{d.nFactura}</td>
                  <td>{d.receptor ?? (d.tipo === '04' ? 'Consumidor final' : '—')}</td>
                  <td className="num">{dinero(d.total, d.moneda)}</td>
                  <td><Pill tono={ESTADO[d.estado].tono}>{ESTADO[d.estado].texto}</Pill></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagina && totalPaginas > 1 && (
        <div className="paginacion">
          <Boton tipo="secundario" deshabilitado={(filtro.pagina ?? 1) <= 1} onClick={() => setFiltro({ ...filtro, pagina: (filtro.pagina ?? 1) - 1 })}>Anterior</Boton>
          <span className="texto-sec">Página {filtro.pagina ?? 1} de {totalPaginas}</span>
          <Boton tipo="secundario" deshabilitado={(filtro.pagina ?? 1) >= totalPaginas} onClick={() => setFiltro({ ...filtro, pagina: (filtro.pagina ?? 1) + 1 })}>Siguiente</Boton>
        </div>
      )}

      {abierto && <Detalle api={api} idEmpresa={idEmpresa} doc={abierto} onCerrar={() => setAbierto(null)} onCambio={cargar} />}
    </section>
  );
}

function NoCargadas(p: { api: FacturacionCrApi; idEmpresa: number; errores: ErrorCargaCr[]; onCambio: (e: ErrorCargaCr[], n: number) => void }) {
  const [cargando, setCargando] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);

  async function recargar(n: number) {
    setCargando(n);
    setError(null);
    try {
      p.onCambio(await p.api.recargarFactura(p.idEmpresa, n), n);
    } catch (e) {
      setError(e);
    } finally {
      setCargando(null);
    }
  }

  return (
    <div className="panel-aviso">
      <h3>Facturas no cargadas ({p.errores.length})</h3>
      <p className="texto-sec">Estas ventas no entraron a facturación electrónica. Corrija la causa y vuelva a cargarlas.</p>
      <ErrorApi error={error} />
      <ul className="lista-errores">
        {p.errores.map((e, i) => (
          <li key={`${e.nFactura}-${i}`}>
            <div>
              <b>Venta {e.nFactura ?? '—'}</b> <span className="texto-sec">{fechaHora(e.fecha)}</span>
              <div className="texto-sec">{e.mensaje}</div>
            </div>
            {e.nFactura !== null && (
              <Boton tipo="secundario" cargando={cargando === e.nFactura} onClick={() => recargar(e.nFactura!)}>Volver a cargar</Boton>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Detalle(p: { api: FacturacionCrApi; idEmpresa: number; doc: DocRef; onCerrar: () => void; onCambio: () => void }) {
  const [d, setD] = useState<DocumentoCrDetalle | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [accion, setAccion] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [confirmar, setConfirmar] = useState(false);
  const { api, idEmpresa, doc: ref, onCerrar } = p;

  useEffect(() => {
    api.documento(idEmpresa, ref).then(setD).catch(setError);
  }, [api, idEmpresa, ref]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onCerrar();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onCerrar]);

  async function ejecutar(nombre: string, fn: () => Promise<DocumentoCrDetalle>, mensaje: string) {
    setAccion(nombre);
    setError(null);
    setAviso(null);
    try {
      setD(await fn());
      setAviso(mensaje);
      p.onCambio();
    } catch (e) {
      setError(e);
    } finally {
      setAccion(null);
      setConfirmar(false);
    }
  }

  async function descargar(clave: string, tipo: 'FIRMADO' | 'RESPUESTA') {
    const nombre = `${clave}-${tipo}`;
    setAccion(nombre);
    setError(null);
    try {
      const blob = await api.descargarArchivo(idEmpresa, clave, tipo);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (tipo === 'FIRMADO' ? '' : 'Respuesta-') + clave + '.xml';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(e);
    } finally {
      setAccion(null);
    }
  }

  const doc = d?.documento;
  const archivosDe = (clave: string | null) => (clave ? d?.archivos.filter((a) => a.clave === clave) ?? [] : []);

  return (
    <div className="cajon-fondo" onClick={onCerrar}>
      <aside className="cajon" role="dialog" aria-modal="true" aria-label="Detalle del comprobante" onClick={(e) => e.stopPropagation()}>
        <div className="cajon__cab">
          <div>
            <p className="eyebrow">{doc ? doc.tipoNombre : 'Comprobante'}</p>
            <h2 className="mono">{doc?.consecutivo ?? `Venta ${ref.nFactura}`}</h2>
          </div>
          <button type="button" className="btn btn--texto" onClick={onCerrar} aria-label="Cerrar">Cerrar ✕</button>
        </div>

        {!d && !error && <div className="cargando">Cargando…</div>}
        <ErrorApi error={error} />
        {aviso && <Alerta tipo="exito" titulo={aviso} />}

        {doc && d && (
          <div className="cajon__cuerpo">
            <div className="estado-linea">
              <Pill tono={ESTADO[doc.estado].tono}>{ESTADO[doc.estado].texto}</Pill>
              <span className="texto-sec">{ESTADO[doc.estado].ayuda}</span>
            </div>

            {doc.error && doc.estado === 'RECHAZADO' && <Alerta tipo="error" titulo="Motivo del rechazo">{doc.error}</Alerta>}
            {doc.estado === 'OTRO' && doc.respuesta && <Alerta tipo="aviso" titulo="Respuesta recibida">{doc.respuesta}</Alerta>}

            {d.puedeReemitir && (
              <div className="panel-aviso">
                {!confirmar ? (
                  <div className="acciones acciones--inicio">
                    <Boton onClick={() => setConfirmar(true)}>Emitir de nuevo</Boton>
                    <span className="texto-sec">Corrija primero la causa del rechazo.</span>
                  </div>
                ) : (
                  <>
                    <p>
                      Se creará un <b>comprobante nuevo</b> con otro consecutivo, tomando los datos actuales de la venta, de sus productos y del
                      emisor. El rechazado queda en el historial.
                    </p>
                    <div className="acciones acciones--inicio">
                      <Boton cargando={accion === 'reemitir'}
                        onClick={() => ejecutar('reemitir', () => api.reemitir(idEmpresa, ref), 'Listo: el comprobante nuevo se enviará a Hacienda en los próximos minutos.')}>
                        Sí, emitir de nuevo
                      </Boton>
                      <Boton tipo="secundario" onClick={() => setConfirmar(false)}>Cancelar</Boton>
                    </div>
                  </>
                )}
              </div>
            )}

            <dl className="meta meta--detalle">
              <dt>Venta (POS)</dt><dd className="mono">{doc.nFactura}</dd>
              <dt>Fecha</dt><dd>{fechaCorta(doc.fecha)}</dd>
              <dt>Cliente</dt><dd>{doc.receptor ?? (doc.tipo === '04' ? 'Consumidor final' : '—')}{doc.receptorId && <span className="mono texto-sec"> · {doc.receptorId}</span>}</dd>
              <dt>Total</dt><dd><b>{dinero(doc.total, doc.moneda)}</b></dd>
              {doc.referencia && (<><dt>Referencia</dt><dd className="mono quiebre">{doc.referencia}{doc.referenciaRazon && <div className="texto-sec">{doc.referenciaRazon}</div>}</dd></>)}
              <dt>Clave</dt><dd className="mono quiebre">{doc.clave ?? 'Se asigna al enviarse a Hacienda'}</dd>
            </dl>

            <h3>Archivos</h3>
            {archivosDe(doc.clave).length === 0 ? (
              <p className="texto-sec">{doc.clave ? 'Los XML aparecen aquí cuando el sistema los envía a Hacienda.' : 'Todavía no se ha enviado.'}</p>
            ) : (
              <div className="acciones acciones--inicio">
                {archivosDe(doc.clave).map((a) => (
                  <Boton key={a.tipo} tipo="secundario" cargando={accion === `${a.clave}-${a.tipo}`} onClick={() => descargar(a.clave, a.tipo)}>
                    {a.tipo === 'FIRMADO' ? 'XML firmado' : 'Respuesta de Hacienda'}
                  </Boton>
                ))}
              </div>
            )}

            <h3>Detalle</h3>
            <div className="tabla-scroll">
              <table className="tabla">
                <thead><tr><th>Descripción</th><th className="num">Cant.</th><th className="num">Precio</th><th className="num">IVA</th><th className="num">Total</th></tr></thead>
                <tbody>
                  {d.lineas.map((l) => (
                    <tr key={l.numero}>
                      <td>{l.descripcion}<div className="mono texto-sec mini">CABYS {l.cabys ?? '—'} · IVA {l.tarifaIva} %{l.exonerado > 0 ? ' · exonerado' : ''}</div></td>
                      <td className="num">{l.cantidad}</td>
                      <td className="num">{num(l.precioUnitario)}</td>
                      <td className="num">{num(l.iva)}</td>
                      <td className="num">{num(l.total)}</td>
                    </tr>
                  ))}
                  {d.otrosCargos.map((o, i) => (
                    <tr key={`oc-${i}`}><td>{o.detalle ?? 'Otro cargo'}<div className="texto-sec mini">Otro cargo</div></td><td /><td /><td /><td className="num">{num(o.monto)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3>Correo al cliente</h3>
            {d.correos.length === 0 ? (
              <p className="texto-sec">
                {doc.estado === 'ACEPTADO' ? (doc.receptorCorreo ? 'Sin envíos registrados.' : 'El cliente no tiene correo registrado.') : 'Se envía cuando Hacienda acepte el comprobante.'}
              </p>
            ) : (
              <ul className="mensajes">
                {d.correos.map((c, i) => (
                  <li key={i}><b>{c.enviado ? 'Enviado' : 'En cola'}</b> <span className="texto-sec">{fechaHora(c.fechaEnvio ?? c.fecha)}</span></li>
                ))}
              </ul>
            )}
            {d.puedeReenviarCorreo && (
              <div className="acciones acciones--inicio">
                <Boton tipo="secundario" cargando={accion === 'correo'}
                  onClick={() => ejecutar('correo', () => api.reenviarCorreo(idEmpresa, ref), 'Correo en cola: se enviará en los próximos minutos.')}>
                  Reenviar correo{doc.receptorCorreo ? ` a ${doc.receptorCorreo}` : ''}
                </Boton>
              </div>
            )}

            {d.reemisiones.length > 0 && (
              <>
                <h3>Intentos rechazados</h3>
                <ul className="mensajes">
                  {d.reemisiones.map((r) => (
                    <li key={r.claveRechazada}>
                      <span className="mono">{r.consecutivo ?? r.claveRechazada}</span> <span className="texto-sec">· {fechaHora(r.fecha)}</span>
                      {r.error && <div className="texto-sec mini">{r.error}</div>}
                      <div className="acciones acciones--inicio">
                        {archivosDe(r.claveRechazada).map((a) => (
                          <Boton key={a.tipo} tipo="texto" cargando={accion === `${a.clave}-${a.tipo}`} onClick={() => descargar(a.clave, a.tipo)}>
                            {a.tipo === 'FIRMADO' ? 'XML firmado' : 'Respuesta de Hacienda'}
                          </Boton>
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}
